import { useEffect, useState } from 'react';
import { operationsApi, type FuelLog } from '../api';

/**
 * Fuel log detail modal with receipt/meter photo preview and a Verify action
 * (WEB-TC-73 / WEB-TC-74). Photos are server-signed on demand.
 */
export function FuelLogDetailModal({
  log,
  assetLabel,
  onClose,
  onVerified,
}: {
  log: FuelLog;
  assetLabel?: string;
  onClose: () => void;
  onVerified: (updated: FuelLog) => void;
}) {
  const [kind, setKind] = useState<'receipt' | 'meter'>('receipt');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);
  const [verified, setVerified] = useState<boolean>(log.verified_at != null);

  const hasPhoto = kind === 'receipt' ? !!log.receipt_photo_path : !!log.meter_photo_path;

  useEffect(() => {
    setPhotoUrl(null);
    setPhotoErr(null);
    if (!hasPhoto) return;
    setPhotoLoading(true);
    void operationsApi
      .fuelPhotoUrl(log.id, kind)
      .then((r) => setPhotoUrl(r.url))
      .catch((e) => setPhotoErr((e as Error).message || 'Could not load photo'))
      .finally(() => setPhotoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, log.id]);

  const verify = async () => {
    setVerifying(true);
    setVerifyErr(null);
    try {
      const updated = await operationsApi.verifyFuelLog(log.id);
      setVerified(updated.verified_at != null);
      onVerified(updated);
    } catch (e) {
      setVerifyErr((e as Error).message || 'Verify failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Fuel log</h2>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {assetLabel && <Row k="Asset" v={assetLabel} />}
          <Row k="Logged" v={new Date(log.logged_at).toLocaleString()} />
          <Row k="Liters" v={`${log.liters.toFixed(1)} L`} />
          <Row k="Cost" v={`${(log.cost_minor / 100).toFixed(2)} ${log.currency}`} />
          <Row
            k="Odometer"
            v={log.odometer != null ? `${log.odometer.toLocaleString()} km` : '—'}
          />
          <Row
            k="Status"
            v={verified ? 'Verified' : log.is_anomaly ? 'Anomaly · unverified' : 'Unverified'}
          />
        </dl>

        {log.is_anomaly && log.anomaly_reason && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            ⚠ {log.anomaly_reason}
          </div>
        )}

        <div>
          <div className="mb-2 flex gap-1">
            <PhotoTab active={kind === 'receipt'} onClick={() => setKind('receipt')}>
              Receipt
            </PhotoTab>
            <PhotoTab active={kind === 'meter'} onClick={() => setKind('meter')}>
              Meter
            </PhotoTab>
          </div>
          <div className="grid min-h-[12rem] place-items-center rounded border border-border bg-muted/20 p-2">
            {!hasPhoto ? (
              <span className="text-sm text-muted-foreground">No {kind} photo on file.</span>
            ) : photoLoading ? (
              <span className="text-sm text-muted-foreground">Loading photo…</span>
            ) : photoErr ? (
              <span className="text-sm text-rose-300">{photoErr}</span>
            ) : photoUrl ? (
              <a href={photoUrl} target="_blank" rel="noreferrer">
                <img
                  src={photoUrl}
                  alt={`${kind} photo`}
                  className="max-h-72 rounded object-contain"
                />
              </a>
            ) : null}
          </div>
        </div>

        {verifyErr && <p className="text-xs text-rose-300">{verifyErr}</p>}

        <div className="flex items-center justify-between pt-1">
          {verified ? (
            <span className="rounded bg-emerald-500/15 px-2 py-1 text-xs text-emerald-300">
              ✓ Verified
              {log.verified_at && ` · ${new Date(log.verified_at).toLocaleString()}`}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Not yet reviewed.</span>
          )}
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={onClose}>
              Close
            </button>
            {!verified && (
              <button className="btn-primary" onClick={verify} disabled={verifying}>
                {verifying ? 'Verifying…' : 'Verify'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-border/40 py-1">
      <dt className="text-muted-foreground">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function PhotoTab({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded px-2.5 py-1 text-xs transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
